import React, {forwardRef, KeyboardEvent, useCallback, useImperativeHandle, useRef, useState} from "react"
import {Input, InputProps} from "./input";
import {Button} from "./button";
import {CloseCircle} from "../assets/svgs/CloseCircle";
import {SearchOutlined} from "../assets/svgs/SearchOutlined";

interface Props extends InputProps {
  onSearch?: (value?: string) => void;
  onValueChange?: (value: string) => void;
}

export interface InputWithResetRef {
  reset: () => void
  focus: () => void
  blur: () => void
}

export const SearchInput = forwardRef<InputWithResetRef, Props>(({
                                                                   onSearch, onValueChange, value, ...props
                                                                 }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState(value as string);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }
  
  const handleReset = () => {
    setInternalValue("")
    onValueChange?.("")
    onSearch?.("")
    handleFocus();
  }
  
  const handleSearch = () => {
    onSearch?.(internalValue);
  }
  
  const handleFocus = () => {
    inputRef.current?.focus();
  }
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
        e.preventDefault()
        onSearch?.(internalValue);
        break
    }
  }, [internalValue]);
  
  useImperativeHandle(ref, () => ({
    reset: handleReset, focus: handleFocus, blur: () => inputRef.current?.blur(),
  }))
  
  return (<Input
    ref={inputRef}
    onChange={handleChange}
    onKeyDown={handleKeyDown}
    icon={!!internalValue ? <Button size={"icon"} variant={"text"} onClick={handleReset}>
      <CloseCircle className={"w-4 h-4 text-black/45"} />
    </Button> : <Button size={"icon"} variant={"text"} onClick={handleSearch}>
      <SearchOutlined className={"w-4 h-4 text-black/45"} />
    </Button>}
    value={internalValue}
    {...props}
  />)
});
